import React from 'react';
import {Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography} from '@mui/material';

/**
 * Модалка «Заказ после корректировки. Список изменений».
 *
 * Показывается на OrderById при каждом открытии заказа с флагом
 * afterCorrection (вернулся из «Корректировок» в «Ожидает сборки»)
 * и непустым диффом: заказы, где правили не позиции (канал продаж,
 * адрес и т.п.), модалку не открывают - причину показывает баннер
 * CorrectionNotice из description.
 * Данные - order.correctionChanges из GET /orders/:id:
 *   { added: [...], removed: [...], changed: [...] },
 * элемент: {assortmentId, type, name, article, multiplicity, uom,
 *           quantity | oldQuantity+newQuantity}.
 *
 * Формат строк (кратность берётся из карточки, при отсутствии раздел
 * с ней пропускается; услуги без артикула показываются по наименованию):
 *   Добавлено: С001-5 кратно 5 кол-во 25 шт.
 *   Удалено: БС002-50 - 3 шт.
 *   Изменено: БС001-100 - 3 шт. → 5 шт.
 */

function fmtQty(value) {
    const n = Number(value);
    if (!isFinite(n)) return String(value ?? '');
    return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(3)));
}

function unitOf(uom) {
    const u = String(uom || '').trim().toLowerCase();
    if (!u || u === 'шт' || u === 'шт.') return 'шт.';
    if (u === 'грамм' || u === 'г' || u === 'г.') return 'г';
    return u;
}

function lineOf(item, kind) {
    const title = item.article && String(item.article).trim() ? item.article : item.name;
    const mult = item.multiplicity && String(item.multiplicity).trim()
        ? ` кратно ${item.multiplicity}`
        : '';
    if (kind === 'changed') {
        const qty = `${fmtQty(item.oldQuantity)} ${unitOf(item.uom)} → ${fmtQty(item.newQuantity)} ${unitOf(item.uom)}`;
        return mult ? `${title}${mult}: ${qty}` : `${title} - ${qty}`;
    }
    const qty = `кол-во ${fmtQty(item.quantity)} ${unitOf(item.uom)}`;
    return mult ? `${title}${mult} ${qty}` : `${title} - ${fmtQty(item.quantity)} ${unitOf(item.uom)}`;
}

export default function CorrectionChangesModal({open, onClose, changes}) {
    const sections = [
        {title: 'Добавлено:', items: changes?.added || [], kind: 'added'},
        {title: 'Удалено:', items: changes?.removed || [], kind: 'removed'},
        {title: 'Изменено:', items: changes?.changed || [], kind: 'changed'}
    ];
    const isEmpty = sections.every(s => s.items.length === 0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Заказ после корректировки. Список изменений:</DialogTitle>
            <DialogContent dividers>
                {isEmpty
                    ? <Typography>Изменений в позициях нет</Typography>
                    : sections.map(section => section.items.length > 0 && (
                        <Box key={section.kind} sx={{mb: 2}}>
                            <Typography component="div" sx={{fontWeight: 'bold'}}>
                                {section.title}
                            </Typography>
                            {section.items.map(item => (
                                <Typography key={`${section.kind}-${item.assortmentId}`} component="div">
                                    {lineOf(item, section.kind)}
                                </Typography>
                            ))}
                        </Box>
                    ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">Закрыть</Button>
            </DialogActions>
        </Dialog>
    );
}
